import React, { useState, useMemo } from "react"
import { ElementToolbar } from "app/editor/element-toolbar"
import { BlockToolbar } from "core/editor/components/block-toolbar"
import { serialize } from "core/editor/serializer"
import {
  SelectionToolbar,
  parse,
  LinkToolbarButton as LinkButton
} from "@convertkit/editor"
import Icon from "@kit/design/icon"
import { Editor } from "core/editor"
import { schema } from "app/editor/schema"
import { throttle, get } from "lodash"
import { Plugins } from "app/editor/plugins"
import { useContainer } from "../use-container"
import { useCurrentUser } from "hooks/use-current-user"
import { useChildElements } from "../utils/use-child-elements"
import { getValue } from "./utils/get-value"
import "./content-editor.css"
import { sanitizeHTML, defaultConfig } from "utils/sanitize-html"

const Link = LinkButton({
  component: ({ menu }) => (
    <button
      className="convertkit-editor-selection-toolbar-button"
      onClick={menu.open}
    >
      <Icon name="link" />
    </button>
  )
})

const buttons = [
  "bold",
  "underline",
  "italic",
  "font-size",
  "alignment",
  "color",
  Link
]

const plugins = Plugins()

export const ContentEditor = ({
  className,
  content,
  name,
  value,
  defaults,
  onChange,
  style,
  editing,
  onClick,
  region,
  order
}) => {
  const container = useContainer()
  const currentUser = useCurrentUser()
  const freeUser = get(currentUser, "access_level.free")
  const showHtmlblocks = container.htmlBlocksAllowed
  const { addChild } = useChildElements("region", region)

  // TODO: Some rearchitecture of the builder could reduce the need to throttle updates here.
  // Currently, we can't have every keystroke updating the root builder state, causing every
  // component to rerender.
  const throttleChange = useMemo(
    () =>
      throttle(
        value => {
          onChange({
            // TODO: This should be removed once forms are fully migrated to use Slate
            content: serialize(value, { target: "web" }),
            value: JSON.stringify(value)
          })
        },
        1000,
        { trailing: true }
      ),
    [onChange]
  )

  const [editorValue, setEditorValue] = useState(() => {
    if (editing) {
      return getValue(value, content, defaults, null, { parse })
    }
  })

  const blocks = [
    "paragraph",
    "image",
    "button",
    "horizontal-rule",
    "blockquote",
    "ordered-list",
    "unordered-list",
    "file",
    "video-embed",
    "countdown_timer",
    "product-button",
    "heading-two",
    "heading-three",
    showHtmlblocks && freeUser && "html-upgrade",
    showHtmlblocks && !freeUser && "html",
    "script",
    ...(region ? ["gallery", "links", "icons", "instagram", "newsletter"] : [])
  ].filter(Boolean)

  const attributes = {
    className,
    style,
    "data-element": name
  }

  const handleClick = (event, editor, next) => {
    next()

    if (
      editor.value.focusBlock &&
      editor.value.focusBlock.type !== "image" &&
      editor.value.focusBlock.type !== "button"
    ) {
      onClick()
    }
  }

  if (!editing) {
    return (
      <div
        {...attributes}
        dangerouslySetInnerHTML={{
          __html: sanitizeHTML(
            getValue(value, content, defaults, {
              serialize,
              options: { target: "web" }
            }),
            {
              allowedTags: defaultConfig.allowedTags.concat([
                "iframe",
                "style"
              ]),
              allowVulnerableTags: true // Prevents console warnings for style tags
            }
          )
        }}
      />
    )
  }

  const handleChange = ({ value }) => {
    setEditorValue(value)
    throttleChange(value)
  }

  return (
    <Editor
      plugins={plugins}
      value={editorValue}
      onChange={handleChange}
      target="web"
      location="builder"
      onClick={handleClick}
      schema={schema}
      placeholder="Enter more content...."
      {...attributes}
    >
      <SelectionToolbar buttons={buttons} />
      <ElementToolbar />
      <BlockToolbar blocks={blocks} addChild={addChild} index={order} />
    </Editor>
  )
}

ContentEditor.componentType = "Text"
